import { useAtomValue } from "jotai";
import { atoms } from "@/state";

interface CodeMatchBadgeProps {
  // The candidate code the user has typed. Empty renders the neutral hint.
  code: string;
  // Copy shown in the mismatch state. Different consumers phrase this
  // differently — the orphan re-assign flow says "still orphan", the gallery
  // re-link flow says "will not attach to a model".
  mismatchLabel: string;
}

// Live badge that resolves a candidate code against the app's model-attach
// rules (exact match, else longest-prefix `${modelCode}-` match).
export const CodeMatchBadge: React.FC<CodeMatchBadgeProps> = ({ code, mismatchLabel }) => {
  const match = useAtomValue(atoms.photos.resolveCodeToModel(code));

  if (!code) {
    return <span className="text-xs text-gray-400 dark:text-gray-600">enter a code…</span>;
  }
  if (!match) {
    return (
      <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-950/40 dark:text-red-300">
        ✗ {mismatchLabel}
      </span>
    );
  }
  return (
    <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-950/40 dark:text-green-300">
      ✓ will attach to model <span className="font-mono">{match.modelCode}</span>
      {match.matchType === "prefix" && " (as a specific configuration)"}
    </span>
  );
};
